import React, {useContext, useState} from 'react';
import {useMutation} from '@apollo/client';
import gql from 'graphql-tag';
import {Form, Formik, FormikHelpers, FormikValues} from 'formik';
import UIInput from '../../../ui/form/UIInput';
import {LanguageId} from '../../../models/utils/Languages';
import {FieldConfigurationFragment} from '../../../scenes/app/formConfiguration/fragments';
import FieldConfiguration from '../../../models/form/FieldConfiguration';
import ExternalButtonSelector from '../components/ExternalButtonSelector';
import withTranslations, {InjectedTranslationsProps} from './withTranslations';
import {SectionId} from '../../../models/form/types';
import {FieldConfigurationQL} from '../../../models/graphql/FieldConfigurationQL';
import LanguageBlock from './LanguageBlock';
import FieldLabel from './FieldLabel';
import ControlBlock from './ControlBlock';
import OptionsList, {optName} from './OptionsList';
import DropdownFormCompanion from './DropdownFormCompanion';
import {ActionType, FormTypeContext} from '../types';

interface Props extends InjectedTranslationsProps {
  sectionId: SectionId | undefined
  defaultLanguage: LanguageId
  configuration?: FieldConfiguration
  onCreateUpdate: (action: ActionType, el?: FieldConfiguration) => void;
}

function DropdownForm({
  sectionId, defaultLanguage, configuration, onCreateUpdate, translations, onAddTranslation, onDeleteTranslation,
}: Props) {
  const [createSubmit] = useMutation<{ field: FieldConfigurationQL }>(createMutation);
  const [updateSubmit] = useMutation<{ field: FieldConfigurationQL }>(updateMutation);
  const [loading, setLoading] = useState(false);
  const formTypeContext = useContext(FormTypeContext);
  const companion = new DropdownFormCompanion(sectionId, translations, formTypeContext, configuration);

  const onFormSubmit = (values: FormikValues, helpersReact: FormikHelpers<FormikValues>) => {
    setLoading(true);
    helpersReact.setSubmitting(true);
    const input = companion.prepareInput(values);
    const submit = configuration ? updateSubmit({variables: input}) : createSubmit({variables: input});
    setTimeout(() => {
      companion.handleFieldSubmitRequest(submit, onCreateUpdate).finally(() => setLoading(false));
      helpersReact.setSubmitting(false);
    }, 400);
  };

  return (
    <>
      <Formik
        initialValues={companion.getInitialFormValues()}
        validationSchema={companion.getValidationObject()}
        onSubmit={onFormSubmit}>
        {({values, errors}) => (
          <Form>
            <FieldLabel/>
            <div className="pt-3">
              {translations.map((languageId, index) =>
                <LanguageBlock langId={languageId} onDelete={onDeleteTranslation}
                  deletable={languageId !== defaultLanguage}
                  error={errors[languageId] !== undefined}
                  opened={index === 0 || !configuration?.getLabel(languageId).length} key={languageId}>
                  <UIInput id={languageId} name={languageId} label="Question"/>
                  <div className="mt-3">
                    <span>Options</span>
                    <OptionsList language={languageId} initialValues={values[optName(languageId)]}/>
                  </div>
                </LanguageBlock>)
              }
              <ExternalButtonSelector className={'pt-3'} options={companion.labelManager.availableLanguages()}
                addItem={onAddTranslation}
                btnTitle={<>Add&nbsp;translation</>}/>
            </div>
            <div className="pt-5">
              <ControlBlock loading={loading} disabled={sectionId === undefined}/>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}

export default withTranslations(DropdownForm);

const createMutation = gql`
  mutation CreateMutation($type: FormType!, $data: DropdownInput!) {
    field: createDropdownField(type: $type, data: $data) {
      ...CompleteFieldConfiguration
    }
  }
  ${FieldConfigurationFragment}
`;

const updateMutation = gql`
  mutation UpdateMutation($id: ID!, $type: FormType!, $data: DropdownInput!) {
    field: updateDropdownField(id: $id, type: $type, data: $data) {
      ...CompleteFieldConfiguration
    }
  }
  ${FieldConfigurationFragment}
`;
